import {
    filter,
    find,
    findIndex,
    has,
    includes,
    map,
    reduce,
    startsWith,
    sumBy,
    upperFirst
} from 'lodash';

import { graphqlFetch, stripTags } from '~/utilities/helpers';
import { getConsent } from '~/utilities/consent';
import { formatPrice, getProductPrice } from '~/utilities/products';
import { pickItemsForCode } from '~/utilities/promos';

import { isLoggedIn } from '~/account/utilities/helpers';
import { getUser } from '~/context/UserContext';
import { sumAddOnPricesExcludingBells } from '~/utilities/bells';

import giftCards from '~/account/api/giftCards';
import subscriptions from '~/account/api/subscriptions';
import physicalProductsApi from '~/account/api/physicalProducts';
import washes from '~/account/api/washes';
import prepaidWashes from '~/account/api/prepaidWashes';

import AmexLogo from '~/images/credit-cards/amex.svg';
import DiscoverLogo from '~/images/credit-cards/discover.png';
import MastercardLogo from '~/images/credit-cards/mastercard.svg';
import VisaLogo from '~/images/credit-cards/visa.png';
import DefaultCard from '~/images/credit-cards/default.svg';

const isPhysicalItem = (item) =>
    item.productType === 'physical' ||
    (item.productType === 'gift-card' && item.isPhysicalGiftCard);

const CART_STORAGE_KEY = 'ab_cart';

export const getCartToken = () => {
    try {
        const cartJson = localStorage.getItem(CART_STORAGE_KEY);
        if (!cartJson) return null;
        const cart = JSON.parse(cartJson);
        return cart?.cart_token || null;
    } catch (error) {
        console.error('Error reading cart token from localStorage:', error);
        return null;
    }
};

export const clearCartToken = () => {
    try {
        localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing cart token from localStorage:', error);
    }
};

const getProductFromCMS = async (
    serviceId,
    location,
    displayName = null,
    slug = null
) => {
    // const queryID =
    const whereClause = displayName
        ? `displayName: "${displayName}"`
        : serviceId
          ? `serviceId: "${serviceId}"`
          : slug
            ? `name: "${slug}"`
            : `name: ""`;
    const query = `query {
            products(where: {${whereClause}}) {
                nodes {
                    serviceId
                    subscriptionPlanId
                    databaseId
                    slug
                    title
                    displayName
                    locations
                    featuredImage {
                        node {
                            sourceUrl
                        }
                    }
                    productInformation {
                        price
                        showVehicleInformationForm
                        bogo {
							discountAmount
							discountType
							enableBogo
							threshold
							prepaidPromotion{
								... on PrepaidPromotion {
									title
									subTitle
									ampId
									effectiveFrom
									effectiveTo
									ordinal
									percentOff
									price
									promotionQuantity
									purchaseLimit
									purchaseQuantity
									savingsLabel
									washTypeIds
								}
							}
						}
                    }
                    stripeId
                    stripePrices(priceGroup: ${location.priceGroup}) {
                        priceGroup
                        label
                        stripeId
                        id
                        unitAmount
                        intervalType
                        intervalCount
                        pricingOptionId
                    }
                    productTypes {
                        nodes {
                            slug
                        }
                    }
                    cleaningCategories {
                        nodes {
                            slug
                        }
                    }
                }
            }
        }`;

    const { products, product: graphQLProduct } = await graphqlFetch(query);

    let product = null;
    if (products) {
        product = find(
            products.nodes,
            ({ locations, displayName: prodDisplayName }) =>
                includes(locations, location.locationId) &&
                (!displayName || prodDisplayName === displayName)
        );
        if (!product && slug && products.nodes.length > 0) {
            product = products.nodes[0];
        }
    } else if (graphQLProduct) {
        product = graphQLProduct;
    }

    return product;
};

export const getShippingSettings = async () => {
    const query = `query {
                siteSettings {
                    siteSettingsFields {
                        shippingRate
                        freeShipping {
                            enabled
                            threshold
                        }
                    }
                }
		}`;

    const {
        siteSettings: {
            siteSettingsFields: {
                shippingRate,
                freeShipping: { enabled, threshold }
            }
        }
    } = await graphqlFetch(query);

    return {
        shippingRate,
        freeShippingEnabled: enabled,
        freeShippingThreshold: threshold
    };
};

export const updateRecipientsIfLoggedIn = async (items, user) => {
    if (!isLoggedIn()) return;
    if (items.length < 1) return;
    for (let i = 0; i < items.length; i++) {
        if (items[i].bogoPrepaidPromotion !== undefined) {
            if (items[i].recipients[0].first_name === '') {
                const splitName = user.name.split(' ');
                const firstName = splitName[0];
                const lastName = splitName[splitName.length - 1];

                items[i].recipients[0].first_name = firstName;
                items[i].recipients[0].last_name = lastName;
            }
            if (items[i].recipients[0].email === '') {
                items[i].recipients[0].email = user.email;
            }
            if (items[i].recipients[0].send_date === '') {
                items[i].recipients[0].send_date = null;
            }
        }
    }
};

export const queryCartItem = async (
    serviceId,
    location,
    displayName = null,
    giftCardValue = null,
    addPromo = false,
    slug = null
) => {
    const product = await getProductFromCMS(
        serviceId,
        location,
        displayName,
        slug
    );
    if (product) {
        const productType = product.productTypes.nodes[0].slug;
        const stripePrice =
            productType === 'subscription'
                ? find(product.stripePrices, {
                      priceGroup: location.priceGroup,
                      intervalCount: 1,
                      intervalType: 'month'
                  })
                : product.stripePrices[0];

        let productObj = {
            serviceId: product?.serviceId,
            subscriptionPlanId: product?.subscriptionPlanId,
            billingFrequencyId: stripePrice?.pricingOptionId,
            pricingGroupId: location.priceGroup,
            id: product.databaseId,
            slug: product.slug,
            databaseId: product.databaseId,
            displayName: product.displayName || null,
            location: {
                id: location.locationId,
                address: location.address
            },
            title: product.displayName ? product.displayName : product.title,
            price:
                giftCardValue ??
                getProductPrice(
                    product,
                    productType,
                    location.priceGroup,
                    false
                ),
            bogoThreshold: product.productInformation.bogo.enableBogo
                ? product.productInformation.bogo.threshold
                : undefined,
            bogoDiscountType: product.productInformation.bogo.enableBogo
                ? product.productInformation.bogo.discountType
                : undefined,
            bogoDiscountAmount: product.productInformation.bogo.enableBogo
                ? product.productInformation.bogo.discountAmount
                : undefined,
            bogoPrepaidPromotion:
                addPromo && product.productInformation.bogo.prepaidPromotion
                    ? product.productInformation.bogo.prepaidPromotion
                    : undefined,
            stripe_id: product.stripeId,
            stripe_price_id: stripePrice?.stripeId,
            price_id: stripePrice?.id,
            image: product.featuredImage?.node.sourceUrl,
            productType,
            vehicle: {
                garage: ''
            },
            showVehicleInformationForm:
                !!product?.productInformation?.showVehicleInformationForm,
            cleaningCategories: product?.cleaningCategories?.nodes || []
        };

        const today = new Date().toISOString();

        if (productObj.bogoPrepaidPromotion && addPromo)
            if (
                productObj.bogoPrepaidPromotion.effectiveFrom <= today &&
                productObj.bogoPrepaidPromotion.effectiveTo >= today
            ) {
                let recipient = [
                    {
                        email: '',
                        send_date: null,
                        first_name: '',
                        last_name: '',
                        message: null,
                        quantity: 1
                    }
                ];

                if (isLoggedIn()) {
                    const userData = await getUser();
                    const name = userData.name;
                    const splitName = name.split(' ', 2);
                    const firstName = splitName[0];
                    const lastName = splitName[1];

                    recipient = [
                        {
                            email: userData.email,
                            send_date: null,
                            first_name: firstName,
                            last_name: lastName,
                            message: null,
                            quantity: 1
                        }
                    ];
                }
                productObj.recipients = recipient;
                productObj.hasRecipient = false;
            }

        return productObj;
    }
    return null;
};

export const createGtagEcommEvent = (
    eventType,
    items,
    additionalValues = {}
) => {
    if (!getConsent().marketing) {
        return;
    }
    if (window.dataLayer) {
        const gtagItems = reduce(
            items,
            (acc, item) => {
                const gtagAddons = map(item.addOns, (addOn) => ({
                    item_id: addOn.id,
                    item_name: addOn.title,
                    quantity: item.quantity,
                    price: addOn.price
                }));
                return [
                    ...acc,
                    {
                        item_id: item.id,
                        item_name: item.title,
                        quantity: item.quantity,
                        price: item.price
                    },
                    ...gtagAddons
                ];
            },
            []
        );
        const eventData = {
            value: sumBy(gtagItems, ({ price }) => price),
            currency: 'USD',
            items: gtagItems,
            ...additionalValues
        };
        window.dataLayer.push({
            event: eventType,
            ecommerce: eventData
        });
    }
};

export const getBogoItems = (
    threshold,
    bogoDiscountType,
    bogoDiscountAmount,
    quantity,
    cartItemId,
    cartItems
) => {
    const bogoItemIndex = findIndex(cartItems, { cartItemId });
    const bogoItem = cartItems[bogoItemIndex];
    cartItems = cartItems.filter(
        ({ cartItemId: itemId }) => !startsWith(itemId, `${cartItemId}-bogo`)
    );

    if (
        quantity >= threshold ||
        (bogoItem.productType === 'digital' && cartItems.length >= threshold)
    ) {
        const totalItemsToAdd =
            bogoItem.productType === 'digital'
                ? Math.floor(quantity / threshold)
                : Math.floor(quantity / threshold);
        let bogoItems = [];
        let price = calculateBogoPrice(
            bogoDiscountType,
            bogoDiscountAmount,
            bogoItem
        );

        for (let i = 0; i < totalItemsToAdd; i++) {
            bogoItems.push({
                ...structuredClone(bogoItem),
                title: bogoItem.bogoPrepaidPromotion
                    ? `${bogoItem.title} - ${bogoItem.bogoPrepaidPromotion.title}`
                    : `${bogoItem.title} - BOGO`,
                quantity: 1,
                bogoItem: true,
                price: price,
                tax: 0,
                priceDisplay: price > 0 ? `$${price}` : 'FREE',
                cartItemId: `${cartItemId}-bogo-${i}`
            });
        }
        cartItems = cartItems.toSpliced(bogoItemIndex + 1, 0, ...bogoItems);
    }
    return cartItems;
};

const calculateBogoPrice = (bogoDiscountType, bogoDiscountAmount, bogoItem) => {
    let finalPrice = 0,
        itemPrice = bogoItem.price,
        price,
        prepaidPromo = bogoItem.bogoPrepaidPromotion,
        prepaidFlat = prepaidPromo
            ? prepaidPromo.price !== null
                ? true
                : false
            : false;
    if (prepaidPromo) {
        switch (prepaidFlat) {
            // discount flat dollar amount
            case true:
                price = itemPrice - prepaidPromo.price;
                break;
            // discount percentage
            case false:
                price = itemPrice - itemPrice * (prepaidPromo.percentOff / 100);
                break;
            default:
                break;
        }
    } else {
        switch (bogoDiscountType) {
            case 'percent':
                price = itemPrice - itemPrice * (bogoDiscountAmount / 100);
                break;
            case 'dollar':
                price = itemPrice - bogoDiscountAmount;
                break;
            default:
                break;
        }
    }

    finalPrice = price >= 0 ? price : 0;
    return Math.round(finalPrice * 100) / 100;
};

export const canGuestCheckout = (items) => {
    const blockingItem = items.find(
        (item) =>
            includes(['subscription', 'digital'], item.productType) ||
            (item.productType === 'gift-card' && !item.isPhysicalGiftCard)
    );
    return !!items.length && !blockingItem;
};

const createPhysicalProductsDraftInvoice = async (
    customerId,
    physicalProducts,
    items,
    promoCode = null
) => {
    const shipping = await calculateShippingCost(items);
    const totals = await reduceCart(new Map(), items, false, true);

    // Handle Shipping
    if (shipping === 0) {
        totals.shipping = 0;
    } else {
        totals.shipping = shipping;
        totals.grandTotal += shipping;
    }

    const physicalItemsInvoiceID = find(
        physicalProducts,
        'invoice_id'
    )?.invoice_id;
    const response =
        await physicalProductsApi.createPhysicalProductsDraftInvoice(
            customerId,
            physicalProducts,
            promoCode,
            physicalItemsInvoiceID,
            totals.shipping * 100
        );
    if (response.success) {
        let subtotal = response.data.subtotal / 100;
        let discount = response.data.discount
            ? response.data.discount / 100
            : 0;
        if (discount) {
            subtotal = subtotal + discount;
        }
        const shipping = {
            ...response.data.shipping,
            amount_total: (response.data.shipping?.amount_total ?? 0) / 100
        };
        return {
            invoice_id: response.data.invoice_id,
            total: response.data.total / 100,
            subTotal: subtotal,
            tax: response.data.tax / 100,
            discount,
            shipping,
            invoice: response.data,
            refreshKey: response.refreshKey
        };
    }
    return {
        error: response.errorCode
    };
};

const validateDiscounts = async (promoCodes, items, customerId) => {
    let physicalProductsInvoice = null;
    let promoCodeErrors = [];
    let promoCodesWithIDs = [];
    let promoCodeErrorMsg = '';
    // Strip any previously-stamped promoCode markers; this run will re-stamp
    // based on the picker's current decision.
    let newItems = items.map((item) => {
        if (!item || !item.promoCode) return item;
        const { promoCode: _omit, ...rest } = item;
        return rest;
    });

    const subscriptionItems = filter(newItems, {
        productType: 'subscription'
    });

    const physicalProducts = filter(newItems, isPhysicalItem);

    const digitalProducts = filter(newItems, { productType: 'digital' });

    for (const promoCode of promoCodes) {
        const { code, applicable_to } = promoCode;

        const appliesToSubscriptions =
            applicable_to === 'all' || applicable_to === 'subscription';
        const appliesToDigital =
            applicable_to === 'all' ||
            applicable_to === 'single_wash' ||
            applicable_to === 'addon';

        const { selectedCartItemIds, notAppliedReason } = pickItemsForCode(
            newItems,
            promoCode
        );
        const selectedSet = new Set(selectedCartItemIds);

        let subscriptionStamped = false;

        if (subscriptionItems.length && appliesToSubscriptions) {
            for (const item of subscriptionItems) {
                if (!selectedSet.has(item.cartItemId)) continue;
                const newItemIndex = findIndex(newItems, {
                    cartItemId: item.cartItemId
                });
                if (newItemIndex > -1) {
                    newItems[newItemIndex].promoCode = { code };
                    subscriptionStamped = true;
                }
            }
        }

        if (physicalProducts.length && !subscriptionStamped) {
            const response = await createPhysicalProductsDraftInvoice(
                customerId,
                physicalProducts,
                items,
                code
            );

            if (!response.error) {
                physicalProductsInvoice = response;
                for (const item of physicalProducts) {
                    const newItemIndex = findIndex(newItems, {
                        cartItemId: item.cartItemId
                    });
                    newItems[newItemIndex].promoCode = {
                        id: response.promo_code_id,
                        code
                    };
                }
            }
        }

        if (digitalProducts.length && appliesToDigital) {
            for (const item of digitalProducts) {
                if (item.bogoPrepaidPromotion) continue;
                if (!has(item, 'vehicle')) {
                    promoCodeErrorMsg = `You must select a vehicle first before you can add a promotional code for ${item.title}`;
                    continue;
                }

                const nonBellsAddOns = filter(
                    item.addOns,
                    (addOn) => !addOn.useBells
                );
                if (applicable_to === 'addon' && !nonBellsAddOns.length)
                    continue;

                const isSelected = selectedSet.has(item.cartItemId);

                const { digitalItem } = await washes.addInvoice(
                    item.location.id,
                    item,
                    isSelected ? code : undefined
                );

                const newItemIndex = findIndex(newItems, {
                    cartItemId: digitalItem.cartItemId
                });

                if (newItemIndex > -1) {
                    newItems[newItemIndex] = isSelected
                        ? { ...digitalItem, promoCode: { code } }
                        : digitalItem;
                }
            }
        }

        const pickerSucceeded = selectedSet.size > 0;
        const physicalSucceeded = !!physicalProductsInvoice;
        const didSucceed = pickerSucceeded || physicalSucceeded;

        if (didSucceed || notAppliedReason) {
            promoCodesWithIDs.push({
                code,
                discount_type: promoCode.discount_type,
                discount_amount: promoCode.discount_amount,
                discount_percentage: promoCode.discount_percentage,
                applicable_to: promoCode.applicable_to,
                stripe_promotion_code_id:
                    promoCode.stripe_promotion_code_id ?? null,
                max_total_redemptions: promoCode.max_total_redemptions ?? null,
                max_redemptions_per_user:
                    promoCode.max_redemptions_per_user ?? null,
                redemption_count: promoCode.redemption_count ?? 0,
                appliedToItemIds: selectedCartItemIds,
                notAppliedReason: notAppliedReason
            });
        } else {
            promoCodeErrors.push(code);
        }
    }

    if (physicalProducts.length && !physicalProductsInvoice) {
        const response = await createPhysicalProductsDraftInvoice(
            customerId,
            physicalProducts,
            items
        );

        if (!response.error) {
            physicalProductsInvoice = response;
        }
    }

    const addInvoiceInfoForPhysicalProduct = () => {
        return map(newItems, (item) => {
            if (isPhysicalItem(item)) {
                const physicalTotal =
                    (item.price + sumAddOnPricesExcludingBells(item.addOns)) *
                    item.quantity;

                const fallback = {
                    subTotal: physicalTotal,
                    total: physicalTotal,
                    discount: 0,
                    shipping: 0,
                    tax: 0
                };

                const physicalInvoice = physicalProductsInvoice ?? fallback;

                return {
                    ...item,
                    invoice_id: physicalInvoice.invoice_id || null
                };
            }

            return item;
        });
    };

    return {
        promoCodeErrorMsg,
        promoCodeErrors,
        physicalProductsInvoice,
        promoCodes: promoCodesWithIDs,
        items: addInvoiceInfoForPhysicalProduct()
    };
};

const calculateShippingCost = async (items) => {
    const { shippingRate, freeShippingEnabled, freeShippingThreshold } =
        await getShippingSettings();

    const physicalProducts = filter(items, isPhysicalItem);

    const subTotal = await reduce(
        physicalProducts,
        async (acc, item) => {
            let cur = await acc;
            return (cur += item.price * item.quantity);
        },
        Promise.resolve(0)
    );

    if (freeShippingEnabled && subTotal >= freeShippingThreshold) {
        return 0;
    }

    return shippingRate;
};

const reduceCart = async (
    bogoCounter,
    newItems,
    physicalProductsInvoice,
    totalsOnly = false
) =>
    await reduce(
        [...newItems],
        async (totals, item, index) => {
            let awaitedTotals = await totals;

            const isBogo = item?.cartItemId?.includes('bogo');
            switch (item.productType) {
                case 'gift-card':
                    if (item.isPhysicalGiftCard) {
                        if (!physicalProductsInvoice) {
                            awaitedTotals.discount += item.discount ?? 0;
                            awaitedTotals.subTotal +=
                                item.subTotal ?? item.price;
                            awaitedTotals.grandTotal +=
                                item.subTotal ?? item.price;
                        }
                        const physGiftShipping =
                            await calculateShippingCost(newItems);
                        if (physGiftShipping === 0) {
                            awaitedTotals.shipping = 0;
                        } else {
                            awaitedTotals.shipping = !physicalProductsInvoice
                                ? physGiftShipping
                                : physicalProductsInvoice.shipping.amount_total;
                            awaitedTotals.grandTotal += !physicalProductsInvoice
                                ? physGiftShipping
                                : 0;
                        }
                        awaitedTotals.items[index] = item;
                        return awaitedTotals;
                    }

                    const { giftItem } = await giftCards.getInvoice(
                        item,
                        item.location.id
                    );

                    const giftDiscount = giftItem.discount ?? 0;
                    const giftSubTotal = giftItem.subtotal ?? item.price;

                    awaitedTotals.discount += giftDiscount;
                    awaitedTotals.subTotal += giftSubTotal * item.quantity;
                    awaitedTotals.grandTotal += giftSubTotal * item.quantity;

                    awaitedTotals.items[index] = giftItem;

                    return awaitedTotals;
                case 'subscription':
                    const { subscriptionItem } =
                        await subscriptions.previewNewSubscriptionInvoice(
                            item,
                            item.location.id
                        );

                    const subscriptionInvoice = subscriptionItem.invoice;
                    const subscriptionTax = subscriptionInvoice?.tax ?? 0;
                    const subscriptionDiscount =
                        subscriptionInvoice?.discount ?? 0;
                    const subscriptionSubTotal =
                        subscriptionInvoice?.subtotal ?? item.price;
                    const subscriptionGrandTotal =
                        subscriptionInvoice?.total ??
                        item.price + subscriptionTax;

                    awaitedTotals.tax += subscriptionTax;
                    awaitedTotals.discount += subscriptionDiscount;
                    awaitedTotals.subTotal += subscriptionSubTotal;
                    awaitedTotals.grandTotal += subscriptionGrandTotal;

                    awaitedTotals.items[index] = subscriptionItem;

                    return awaitedTotals;
                case 'physical':
                    if (!physicalProductsInvoice) {
                        awaitedTotals.discount += item.discount ?? 0;
                        awaitedTotals.tax += item.tax ?? 0;
                        awaitedTotals.subTotal += item.subTotal ?? item.price;
                        awaitedTotals.grandTotal += item.total ?? item.price;
                    }

                    const shipping = await calculateShippingCost(newItems);

                    if (shipping === 0) {
                        awaitedTotals.shipping = 0;
                    } else {
                        awaitedTotals.shipping = !physicalProductsInvoice
                            ? shipping
                            : physicalProductsInvoice.shipping.amount_total;
                        awaitedTotals.grandTotal += !physicalProductsInvoice
                            ? shipping
                            : 0;
                    }
                    awaitedTotals.items[index] = item;
                    return awaitedTotals;
                case 'digital':
                    if (item.bogoPrepaidPromotion && !isBogo) {
                        if (bogoCounter.has(item.id)) {
                            bogoCounter.set(
                                item.id,
                                bogoCounter.get(item.id) + 1
                            );
                        } else {
                            bogoCounter.set(item.id, 1);
                        }

                        if (isLoggedIn()) return awaitedTotals;

                        const digitalTotal =
                            (item.price +
                                sumAddOnPricesExcludingBells(item.addOns)) *
                            item.quantity;
                        const digitalItem = item;

                        const digitalComputedTotal = isBogo
                            ? digitalTotal
                            : digitalTotal;
                        const digitalInvoice = digitalItem.invoice;
                        const digitalTax = digitalInvoice?.tax ?? 0;
                        const digitalDiscount = digitalInvoice?.discount ?? 0;
                        const digitalSubTotal =
                            digitalInvoice?.subtotal ?? digitalComputedTotal;
                        const digitalGrandTotal =
                            digitalInvoice?.total ?? digitalComputedTotal;

                        awaitedTotals.tax += digitalTax;
                        awaitedTotals.discount += digitalDiscount;
                        awaitedTotals.subTotal += digitalSubTotal;
                        awaitedTotals.grandTotal += digitalGrandTotal;

                        awaitedTotals.items[index] = digitalItem;

                        return awaitedTotals;
                    } else if (item.bogoPrepaidPromotion && isBogo) {
                        if (!isLoggedIn()) {
                            awaitedTotals.subTotal += item.price;
                            awaitedTotals.grandTotal += item.price;

                            awaitedTotals.items[index] = item;
                        }

                        return awaitedTotals;
                    } else {
                        if (!totalsOnly) {
                            const { digitalItem, digitalTotal } =
                                await washes.addInvoice(item.location.id, item);

                            const digitalComputedTotal = isBogo
                                ? digitalTotal
                                : digitalTotal;
                            const digitalInvoice = digitalItem.invoice;
                            const digitalTax = digitalInvoice?.tax ?? 0;
                            const digitalDiscount =
                                digitalInvoice?.discount ?? 0;
                            const digitalSubTotal =
                                digitalInvoice?.subtotal ??
                                digitalComputedTotal;
                            const digitalGrandTotal =
                                digitalInvoice?.total ?? digitalComputedTotal;

                            awaitedTotals.tax += digitalTax;
                            awaitedTotals.discount += digitalDiscount;
                            awaitedTotals.subTotal += digitalSubTotal;
                            awaitedTotals.grandTotal += digitalGrandTotal;

                            awaitedTotals.items[index] = digitalItem;
                        } else {
                            awaitedTotals.items[index] = item;
                        }

                        return awaitedTotals;
                    }

                default:
                    console.error(`Unknown product type: ${item?.productType}`);
                    awaitedTotals.subTotal += item?.price * item?.quantity;
                    awaitedTotals.grandTotal += item?.price * item?.quantity;
                    awaitedTotals.items[index] = item ?? {};

                    return awaitedTotals;
            }
        },
        Promise.resolve({
            subTotal: 0,
            tax: 0,
            shipping: 0,
            grandTotal: 0,
            discount: 0,
            items: newItems
        })
    );

export const getTotals = async (items, customerId, discounts = []) => {
    const {
        items: newItems,
        physicalProductsInvoice,
        promoCodeErrors,
        promoCodes,
        promoCodeErrorMsg
    } = await validateDiscounts(discounts, items, customerId);

    let bogoCounter = new Map();
    let prepaidInvoices = {};

    const totals = await reduceCart(
        bogoCounter,
        newItems,
        physicalProductsInvoice
    );

    if (bogoCounter.size > 0 && isLoggedIn()) {
        const bogoIterator = bogoCounter[Symbol.iterator]();

        for (const item of bogoIterator) {
            const promoItemIndex = findIndex(
                totals.items,
                (x) =>
                    x.bogoPrepaidPromotion !== undefined &&
                    !x?.cartItemId?.includes('bogo') &&
                    x.id === item[0]
            );

            const promoItem = totals.items[promoItemIndex];

            const qty = item[1];

            const promoInvoiceItemIndex = findIndex(
                totals.items,
                (x) =>
                    x.bogoPrepaidPromotion !== undefined &&
                    x.invoice !== undefined &&
                    !x?.cartItemId?.includes('bogo') &&
                    x.id === item[0]
            );

            if (promoInvoiceItemIndex < 0) {
                const { prepaidWash } = await prepaidWashes.createDraftInvoice(
                    promoItem.location.address.postalCode,
                    promoItem.location.id,
                    promoItem,
                    qty
                );
                prepaidInvoices[prepaidWash.invoice.stripe_invoice_id] =
                    prepaidWash.invoice;
                addInvoiceToAllPromo(totals.items, prepaidWash, qty);
            } else if (
                promoInvoiceItemIndex >= 0 &&
                totals.items[promoInvoiceItemIndex].totalQty !== qty
            ) {
                const promoInvoiceItem = totals.items[promoInvoiceItemIndex];
                const { updatedWash } = await prepaidWashes.updateDraftInvoice(
                    promoInvoiceItem.location.address.postalCode,
                    promoInvoiceItem.location.id,
                    promoInvoiceItem,
                    qty
                );
                prepaidInvoices[updatedWash.invoice.stripe_invoice_id] =
                    updatedWash.invoice;
                addInvoiceToAllPromo(totals.items, updatedWash, qty);
            } else {
                prepaidInvoices[
                    totals.items[
                        promoInvoiceItemIndex
                    ].invoice.stripe_invoice_id
                ] = totals.items[promoInvoiceItemIndex].invoice;
            }
        }
    }

    if (Object.keys(prepaidInvoices).length) {
        totals.discount +=
            sumBy(Object.values(prepaidInvoices), 'discount') ?? 0;
        totals.tax += sumBy(Object.values(prepaidInvoices), 'tax') ?? 0;
        totals.subTotal +=
            sumBy(Object.values(prepaidInvoices), (invoice) =>
                Math.trunc(invoice.subtotal)
            ) ?? 0;
        totals.grandTotal +=
            sumBy(Object.values(prepaidInvoices), (invoice) =>
                Math.trunc(invoice.total)
            ) ?? 0;
    }

    if (physicalProductsInvoice) {
        totals.shipping = physicalProductsInvoice.shipping.amount_total ?? 0;
        totals.discount += physicalProductsInvoice.discount ?? 0;
        totals.tax += physicalProductsInvoice.tax ?? 0;
        totals.subTotal += physicalProductsInvoice.subTotal;
        totals.grandTotal += physicalProductsInvoice.total;
    }

    if (isLoggedIn()) {
        const {
            data: { balance }
        } = await giftCards.getWalletBalance();
        if (balance) {
            const giftBalance = balance * -1;
            totals.giftCardBalance =
                totals.grandTotal > giftBalance
                    ? giftBalance
                    : totals.grandTotal;
            totals.grandTotal -= totals.giftCardBalance;
        }
    }

    return { ...totals, promoCodeErrors, promoCodes, promoCodeErrorMsg };
};

export const getCreditCardLogo = (brand) => {
    switch (brand) {
        case 'visa':
            return VisaLogo;
        case 'mastercard':
            return MastercardLogo;
        case 'amex':
            return AmexLogo;
        case 'discover':
            return DiscoverLogo;
        default:
            return DefaultCard;
    }
};

export const getCouponErrorMessage = (error) => {
    switch (error) {
        case 'COUPON_NOT_FOUND':
            return 'Coupon not found';
        case 'PURCHASE_TYPE':
            return 'No products in your cart match the criteria for the promo code.';
        default:
            return error;
    }
};

export const getCartErrorMessage = (error, items, refunds) => {
    const { cartItemId, newPrice } = error;
    const cartItem = find(items, { cartItemId });
    const refundObj = find(refunds, { cartItemId });
    const title = stripTags(cartItem?.title || 'the item');

    const addons = cartItem?.addOns ?? [];
    const addonPrice = sumBy(addons, 'price');
    const cartPrice = cartItem?.price ?? 0;
    const tax = cartItem?.tax ?? 0;
    const price = cartPrice + addonPrice + tax;
    const priceDisplay = `$${price.toFixed(2)}`;

    let actionMsg = 'Please try again later.';

    if (refundObj) {
        const { refund } = refundObj;
        const failedAmount = refund?.amount
            ? `$${(refund.amount / 100).toFixed(2)}`
            : priceDisplay;
        const otherItems = items.filter(
            (item) => item.cartItemId !== cartItemId
        );
        const otherItemsTotal = otherItems.reduce((sum, item) => {
            const itemAddons = item.addOns ?? [];
            const itemAddonPrice = sumBy(itemAddons, 'price');
            return sum + (item.price ?? 0) + itemAddonPrice + (item.tax ?? 0);
        }, 0);
        const chargedAmount =
            otherItemsTotal > 0 ? `$${otherItemsTotal.toFixed(2)}` : null;

        if (refund?.status === 'succeeded') {
            actionMsg = `We sincerely apologize for the inconvenience. A refund of ${failedAmount} has been issued for this item.`;
            if (chargedAmount) {
                actionMsg += ` Your card was charged ${chargedAmount} for the other item(s) in your cart.`;
            }
        } else {
            actionMsg = `We sincerely apologize for the inconvenience. This item (${failedAmount}) was not charged.`;
            if (chargedAmount) {
                actionMsg += ` Your card was charged ${chargedAmount} for the other item(s) in your cart.`;
            }
        }
    }

    switch (error.error) {
        case 'INVOICE_ITEM_NOT_FOUND':
            return `${upperFirst(
                title
            )} is not currently available. Please try again later.`;
        case 'INVOICE_PRICE_MISMATCH':
            return `The price of ${title} has changed to ${formatPrice(
                newPrice
            )} since adding it to your cart. Please confirm the new price before proceeding.`;
        default:
            let errorString = `There was an error while processing your purchase of the ${title}. ${actionMsg}`;
            const recipientIndex = findIndex(items, { hasRecipient: true });
            if (recipientIndex !== -1) {
                errorString += ` Please note: your gift(s) may not have sent to the intended recipient(s).`;
            }
            return errorString;
    }
};

const addInvoiceToAllPromo = (items, promoInvoiceItem, bogoCounter) => {
    for (let i = 0; i < items.length; i++) {
        if (
            items[i].bogoPrepaidPromotion !== undefined &&
            !items[i]?.cartItemId?.includes('bogo') &&
            items[i].id === promoInvoiceItem.id
        ) {
            items[i].invoice = promoInvoiceItem.invoice;
            items[i].totalQty = bogoCounter;
        }
    }
};
